import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { LayoutData, PageContent } from '@/types/content'
import type { AboutData } from '@/lib/server/content/archive'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cms } from '@/lib/cms'
import { formatDate } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { JsonLd, absoluteUrl } from '@/components/site/json-ld'
import { ButtonLink } from '@/components/kit/button'
import { Picture } from '@/components/media/picture'
import { MoonPhase, phaseForIndex } from '@/components/media/moon-phase'
import { TwoWorlds } from '@/components/home/two-worlds'
import { SocialLinks } from '@/components/site/social-links'
import RichText from '@/components/cms/rich-text'
import { getLayout, getPage } from '@/lib/server/content/site'
import { getAboutData } from '@/lib/server/content/archive'
import { setPageCache } from '@/lib/server/page'

interface AboutPageProps {
    layout: LayoutData
    page: PageContent | null
    about: AboutData
}

const AboutPage: FC<AboutPageProps> = ({ layout, page, about }): ReactNode => {
    const { artist, stats, timeline, artworks, genres } = about
    const statItems = [
        { label: 'Songs', value: stats.songs },
        { label: 'Albums & EPs', value: stats.albums },
        { label: 'Writings', value: stats.writings },
        { label: 'Films', value: stats.videos },
        { label: 'Languages', value: stats.languages }
    ].filter(item => item.value > 0)

    return (
        <Layout layout={layout} overlayHeader>
            <Seo
                title={page?.seo_title ?? 'About'}
                description={page?.seo_description ?? artist.intro}
                ogImage={page?.og_image ?? artist.portrait ?? layout.settings.og_image}
                ogImageAlt={page?.og_image_alt ?? artist.portrait_alt}
                origin={layout.origin}
                path='/about'
                type='profile'
            />

            <JsonLd
                id='about'
                data={{
                    '@context': 'https://schema.org',
                    '@type': 'ProfilePage',
                    url: `${layout.origin}/about`,
                    mainEntity: {
                        '@type': 'MusicGroup',
                        name: artist.name,
                        description: artist.intro ?? undefined,
                        image: absoluteUrl(layout.origin, artist.portrait),
                        genre: genres.map(genre => genre.name)
                    }
                }}
            />

            <section aria-labelledby='about-title' className='relative flex min-h-[92svh] items-end overflow-hidden pb-20 pt-40'>
                <div className='absolute inset-0' aria-hidden='true'>
                    {artist.portrait && <Picture src={artist.portrait} alt='' priority grade='night' sizes='100vw' />}
                    <div className='absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/30' />
                    <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(114,43,59,0.3),transparent_55%)]' />
                </div>

                <div className='shell relative'>
                    <p className='eyebrow text-gold animate-in fade-in duration-700'>
                        {page?.eyebrow ? <span {...cms('pages', page.id, 'eyebrow')}>{page.eyebrow}</span> : 'About the artist'}
                    </p>

                    <h1 id='about-title' className='mt-6 max-w-5xl font-heading text-5xl font-light leading-[1] animate-in fade-in slide-in-from-bottom-3 duration-1000 sm:text-7xl lg:text-8xl'>
                        {page?.heading ? <span {...cms('pages', page.id, 'heading')}>{page.heading}</span> : 'The spaces between emotions'}
                    </h1>

                    {artist.opening_quote && (
                        <blockquote className='mt-10 max-w-3xl border-l border-gold/50 pl-6 font-heading text-2xl italic leading-snug text-ivory/85 sm:text-3xl'>
                            <span {...cms('artist', artist.id, 'opening_quote')}>{artist.opening_quote}</span>
                        </blockquote>
                    )}

                    {artist.signature && (
                        <p className='mt-6 font-script text-4xl text-gold sm:text-5xl'>
                            <span {...cms('artist', artist.id, 'signature')}>{artist.signature}</span>
                        </p>
                    )}
                </div>
            </section>

            <section id='identity' aria-labelledby='identity-title' className='py-24 sm:py-32'>
                <div className='shell grid gap-14 lg:grid-cols-12 lg:gap-20'>
                    <div className='lg:col-span-4'>
                        <p className='font-brand text-xs tracking-[0.2em] text-gold/70'>I</p>
                        <h2 id='identity-title' className='mt-3 font-heading text-4xl font-light sm:text-5xl'>
                            The identity
                        </h2>

                        {page?.intro && (
                            <p className='mt-6 text-base leading-relaxed text-muted-foreground'>
                                <span {...cms('pages', page.id, 'intro')}>{page.intro}</span>
                            </p>
                        )}

                        <SocialLinks socials={artist} className='mt-8' />
                    </div>

                    <div className='lg:col-span-8'>
                        {artist.bio
                            ? <RichText html={artist.bio} className='font-heading text-xl leading-relaxed text-ivory/85 sm:text-2xl' />
                            : artist.intro && (
                                <p className='font-heading text-2xl leading-relaxed text-ivory/85'>
                                    <span {...cms('artist', artist.id, 'intro')}>{artist.intro}</span>
                                </p>
                            )}
                    </div>
                </div>
            </section>

            <TwoWorlds artist={artist} />

            {artist.philosophy && (
                <section id='philosophy' aria-labelledby='philosophy-title' className='theme-paper py-24 sm:py-32'>
                    <div className='shell grid gap-14 lg:grid-cols-12 lg:gap-20'>
                        <div className='lg:col-span-4'>
                            <p className='font-brand text-xs tracking-[0.2em] text-burgundy/70'>II</p>
                            <h2 id='philosophy-title' className='mt-3 font-heading text-4xl font-light sm:text-5xl'>
                                Philosophy
                            </h2>
                        </div>

                        <RichText html={artist.philosophy} className='font-heading text-xl leading-relaxed text-ink sm:text-2xl lg:col-span-8' />
                    </div>
                </section>
            )}

            {artist.journey && (
                <section id='journey' aria-labelledby='journey-title' className='py-24 sm:py-32'>
                    <div className='shell grid gap-14 lg:grid-cols-12 lg:gap-20'>
                        <div className='lg:col-span-4'>
                            <p className='font-brand text-xs tracking-[0.2em] text-gold/70'>III</p>
                            <h2 id='journey-title' className='mt-3 font-heading text-4xl font-light sm:text-5xl'>
                                The creative journey
                            </h2>
                        </div>

                        <div className='lg:col-span-8'>
                            <RichText html={artist.journey} className='text-lg leading-relaxed text-ivory/80' />

                            {timeline.length > 0 && (
                                <div className='mt-14'>
                                    <p className='eyebrow text-gold'>Recent chapters</p>

                                    <ol className='mt-6 border-l border-white/10'>
                                        {timeline.map(item => (
                                            <li key={item.key} className='relative pb-8 pl-8 last:pb-0'>
                                                <span className='absolute -left-[5px] top-1.5 size-2.5 rounded-full bg-gold' aria-hidden='true' />
                                                <p className='text-xs uppercase tracking-[0.16em] text-muted-foreground'>
                                                    <time dateTime={item.date}>{formatDate(item.date, 'month')}</time> &middot; {item.label}
                                                </p>
                                                <p className='mt-1 font-heading text-2xl'>
                                                    {item.href
                                                        ? <Link href={item.href} className='transition hover:text-gold'><span {...cms(item.table, item.id, 'title')}>{item.title}</span></Link>
                                                        : <span {...cms(item.table, item.id, 'title')}>{item.title}</span>}
                                                </p>
                                            </li>
                                        ))}
                                    </ol>

                                    <Link href='/timeline' className='group mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-gold transition hover:text-ivory'>
                                        The full timeline
                                        <ArrowRight className='size-4 transition group-hover:translate-x-1' aria-hidden='true' />
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {(artist.disciplines.length > 0 || genres.length > 0) && (
                <section id='disciplines' aria-labelledby='disciplines-title' className='border-t border-white/[0.06] py-24 sm:py-32'>
                    <div className='shell'>
                        <p className='font-brand text-xs tracking-[0.2em] text-gold/70'>IV</p>
                        <h2 id='disciplines-title' className='mt-3 font-heading text-4xl font-light sm:text-5xl'>
                            Musical disciplines
                        </h2>

                        {artist.disciplines.length > 0 && (
                            <ul className='mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2 lg:grid-cols-4'>
                                {artist.disciplines.map((discipline, index) => (
                                    <li key={discipline} className='flex flex-col gap-6 bg-obsidian p-7'>
                                        <MoonPhase phase={phaseForIndex(index, artist.disciplines.length)} size={28} />
                                        <span className='font-heading text-2xl leading-tight'>{discipline}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {genres.length > 0 && (
                            <div className='mt-14'>
                                <p className='eyebrow text-muted-foreground'>Across every sound</p>

                                <ul className='mt-5 flex flex-wrap gap-2'>
                                    {genres.map(genre => (
                                        <li key={genre.id}>
                                            <Link href={`/music?genre=${genre.slug}`} className='inline-flex rounded-full border border-ivory/12 px-4 py-2 text-sm text-ivory/75 transition hover:border-gold/50 hover:text-ivory'>
                                                <span {...cms('genres', genre.id, 'name')}>{genre.name}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {statItems.length > 1 && (
                <section aria-label='The work in numbers' className='border-t border-white/[0.06] py-20'>
                    <dl className='shell grid grid-cols-2 gap-10 sm:grid-cols-3 lg:grid-cols-5'>
                        {statItems.map(item => (
                            <div key={item.label}>
                                <dt className='eyebrow text-muted-foreground'>{item.label}</dt>
                                <dd className='mt-3 font-brand text-5xl text-ivory'>{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                </section>
            )}

            {artworks.length > 0 && (
                <section id='artwork' aria-labelledby='artwork-title' className='border-t border-white/[0.06] py-24'>
                    <div className='shell'>
                        <div className='flex flex-wrap items-end justify-between gap-6'>
                            <h2 id='artwork-title' className='font-heading text-4xl font-light sm:text-5xl'>
                                Images that live beside the music
                            </h2>

                            <Link href='/gallery' className='group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-gold transition hover:text-ivory'>
                                The gallery
                                <ArrowRight className='size-4 transition group-hover:translate-x-1' aria-hidden='true' />
                            </Link>
                        </div>

                        <div className='mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4'>
                            {artworks.map(item => (
                                <Link key={item.key} href='/gallery' className='group relative block aspect-[3/4] overflow-hidden rounded-md'>
                                    {item.image && <Picture src={item.image} alt={item.image_alt ?? item.title} sizes='(min-width: 1024px) 25vw, 50vw' imgClassName='transition duration-[1400ms] ease-cinematic group-hover:scale-105' />}
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section aria-label='Get in touch' className='border-t border-white/[0.06] py-24 text-center'>
                <div className='shell'>
                    <p className='font-heading text-4xl font-light sm:text-5xl'>
                        For collaborations, media and licensing.
                    </p>

                    <div className='mt-10 flex justify-center'>
                        <ButtonLink href='/contact' size='lg'>
                            Get in touch
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<AboutPageProps> = async ctx => {
    const [layout, page, about] = await Promise.all([getLayout(ctx.req), getPage('about'), getAboutData()])

    setPageCache(ctx)

    return { props: { layout, page, about } }
}

export default AboutPage
